import { useId, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Picker } from '@orgazone/ui-web';
import { api, INBOUND_MSG_TYPES, type ApiMapping, type ApiSupplierRow } from '../api';
import { Icon } from '../components/icons';
import { MappingSheet, NewMappingModal } from '../components/MappingModals';
import { TypeTag, TypeTile, typeTint } from '../components/message-type';
import { Btn, Card, DataGate, PageHead } from '../components/ui';
import { flowLabel, inServiceLabel, libraryGroups, mappingCount, mappingInReview, supplierCount } from '../mapping-rules';
import { useApp } from '../state';
import { useResource } from '../useResource';

// Mappings (§6) — LA BIBLIOTHÈQUE des mappings partagés, rangée par type de message. Un mapping traduit un flux
// (factures XML, catalogue positionnel…) et sert à autant de fournisseurs qu'il en faut ; une nouvelle version
// s'applique à tous à la fois, et la précédente reste disponible pour revenir en arrière.
// ⚠️ AUCUNE COUVERTURE FOURNISSEUR × TYPE ICI : elle se lit et se règle sur la fiche du fournisseur (onglet Mappings),
//    où se fait le rattachement. Cet écran montre les mappings eux-mêmes, et combien de fournisseurs en dépendent.

/** La grille des lignes : mapping, version en service, nombre de fournisseurs, flèche. */
const COLS = 'minmax(0,1fr) 124px 104px 16px';
const colHead = { fontSize: 'var(--t-sub)', fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--ink-500)' } as const;

export function Mappings() {
  const fieldId = useId();
  const { guard } = useApp();
  const nav = useNavigate();
  const [query, setQuery] = useState('');
  const [types, setTypes] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  // ⚠️ Les deux lectures forment UN chargement : la fiche d'un mapping dit lesquels de ses fournisseurs sont en pause.
  //    « À revoir » vient avec le mapping (le serveur compte les messages que SA traduction a bloqués).
  const { state, data, reload, error } = useResource(async (signal) => {
    const [mappings, suppliers] = await Promise.all([
      api.get<ApiMapping[]>('/admin/mappings', signal),
      api.get<ApiSupplierRow[]>('/admin/suppliers', signal),
    ]);
    return { mappings, suppliers };
  });
  const groups = useMemo(() => (data ? libraryGroups(data.mappings, query, types) : []), [data, query, types]);
  const pausedIds = useMemo(() => new Set((data?.suppliers ?? []).filter((s) => !s.active).map((s) => s.id)), [data]);
  const total = data?.mappings.length ?? 0;

  const pageHead = (
    <PageHead title="Mappings"
      sub="Un mapping traduit un flux d’un fournisseur vers le format interne. Il peut servir à plusieurs fournisseurs — on le rattache depuis leur fiche : une nouvelle version s’applique à tous à la fois, et l’ancienne reste disponible pour revenir en arrière."
      actions={<Btn icon="plus" onClick={() => { if (guard()) setCreating(true); }}>Nouveau mapping</Btn>} />
  );

  return (
    <DataGate state={state} onRetry={reload} error={error} head={pageHead}>
      <Card style={{ overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--ink-200)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div className="field-search" style={{ maxWidth: 320, minWidth: 170, flex: '1 1 220px' }}>
            <Icon name="search" size={15} />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Nom ou flux" aria-label="Rechercher un mapping" />
          </div>
          <Picker id={`${fieldId}-types`} label="Type" allLabel="Tous les types" multiple panelWidth={340}
            options={INBOUND_MSG_TYPES.map((t) => ({ value: t.key, label: t.label, hint: t.key }))}
            value={types} onChange={setTypes} />
          <span style={{ marginLeft: 'auto', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', whiteSpace: 'nowrap' }}>{mappingCount(total)}</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: COLS, gap: 12, alignItems: 'center', padding: '8px 16px', borderBottom: '1px solid var(--ink-200)', background: 'var(--ink-50)' }}>
          <span style={colHead}>Mapping</span><span style={colHead}>En service</span><span style={colHead}>Fournisseurs</span><span />
        </div>

        {groups.map((g) => (
          <section key={g.type} aria-label={g.label}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '12px 16px 8px', borderBottom: '1px solid var(--ink-100)', flexWrap: 'wrap', background: typeTint(g.type).wash }}>
              <TypeTag type={g.type} />
              <span style={{ fontSize: 'var(--t-note)', fontWeight: 500, color: 'var(--ink-700)' }}>{g.label}</span>
              <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>· {mappingCount(g.mappings.length)}</span>
            </div>
            {g.mappings.map((m) => {
              const review = mappingInReview(m);
              const n = m.suppliers.length;
              return (
                <button key={m.id} type="button" onClick={() => setOpenId(m.id)} className="row-nav"
                  style={{ display: 'grid', gridTemplateColumns: COLS, gap: 12, padding: '11px 16px', borderTop: 'none', borderBottom: '1px solid var(--ink-100)', font: 'inherit', color: 'inherit' }}>
                  <span style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 11 }}>
                    <TypeTile type={m.canonicalType} />
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-900)', lineHeight: 1.35, overflowWrap: 'break-word', minWidth: 0 }}>{m.name}</span>
                        {review && <span style={{ display: 'inline-flex', alignItems: 'center', height: 20, padding: '0 8px', borderRadius: 'var(--r-pill)', fontSize: 'var(--t-meta)', fontWeight: 600, background: 'var(--amber-bg)', color: 'var(--amber-fg)' }}>à revoir</span>}
                      </span>
                      <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', fontFamily: 'var(--font-mono)', marginTop: 2, lineHeight: 1.45, overflowWrap: 'break-word' }}>{flowLabel(m)}</span>
                    </span>
                  </span>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-note)', fontWeight: 700, color: 'var(--ink-800)' }}>v{m.currentVersion}</span>
                    <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>{inServiceLabel(m)}</span>
                  </span>
                  <span style={{ fontSize: 'var(--t-note)', fontWeight: n ? 500 : 600, color: n ? 'var(--ink-700)' : 'var(--amber-fg)' }}>{supplierCount(n)}</span>
                  <span style={{ display: 'flex', justifyContent: 'flex-end', color: 'var(--ink-400)' }}><Icon name="arrowR" size={14} /></span>
                </button>
              );
            })}
          </section>
        ))}

        {groups.length === 0 && (
          <div style={{ padding: '28px 16px', textAlign: 'center', fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>
            {total === 0 ? 'Aucun mapping — créez-en un avec « Nouveau mapping ».' : 'Aucun mapping ne correspond à cette recherche.'}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '11px 16px', background: 'var(--ink-50)', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', lineHeight: 1.5 }}>
          <Icon name="info" size={14} /><span>Les sortants (SALES_REPORT, INVENTORY_REPORT) sont construits par la plateforme : ils n’ont pas de mapping.</span>
        </div>
      </Card>

      {creating && <NewMappingModal preset={{}} onClose={() => setCreating(false)} onCreated={async () => { setCreating(false); await reload(); }} />}
      {openId && (
        <MappingSheet id={openId} pausedIds={pausedIds} onClose={() => setOpenId(null)} onChanged={reload}
          onOpenSupplier={(sid) => nav(`/fournisseurs?id=${encodeURIComponent(sid)}&onglet=mappings`)} />
      )}
    </DataGate>
  );
}
